import { AxiosError } from 'axios';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import {
  createTransferContract,
  deleteTransferContract,
  fetchTransferContract,
  fetchTransferContracts,
  IFetchTransferContractsParams,
  updateTransferContract,
} from '../../services/api/TransferContract';
import { IPaginationResponseDto, ISuccessResult, ITransferContract } from '../../services/types';
import useBackdrop from '../useBackdrop';
import useSnackbar from '../useSnackbar';

enum QueryKey {
  AllTransferContracts = 'allTransferContracts',
  TransferContract = 'TransferContract',
}

interface IUseTransferContractMutation {
  create: UseMutationResult<ITransferContract, Error, Partial<ITransferContract>>;
  update: UseMutationResult<ITransferContract, Error, Partial<ITransferContract>>;
  remove: UseMutationResult<ISuccessResult<ITransferContract>, Error, Partial<ITransferContract>>;
}

export const useTransferContracts = (
  page = 1,
  limit = 20,
  params: IFetchTransferContractsParams,
): UseQueryResult<IPaginationResponseDto<ITransferContract>, AxiosError> =>
  useQuery<IPaginationResponseDto<ITransferContract>, AxiosError>(
    [QueryKey.AllTransferContracts, { page, limit, ...params }],
    async () => {
      const [fetchPromise] = fetchTransferContracts(page, limit, params);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    {
      onError: e => {
        console.log(e.response ? e.response.data.message : e.message);
      },
      keepPreviousData: true,
    },
  );

export const useTransferContract = (id?: number): UseQueryResult<ITransferContract | undefined, AxiosError> =>
  useQuery<ITransferContract | undefined, AxiosError>(
    [QueryKey.TransferContract, id],
    async () => {
      if (!id) {
        return;
      }

      const [fetchPromise] = fetchTransferContract(id);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    {
      onError: e => {
        console.log(e.response ? e.response.data.message : e.message);
      },
    },
  );

export const useTransferContractMutation = (): IUseTransferContractMutation => {
  const snackbar = useSnackbar();
  const backdrop = useBackdrop();
  const queryClient = useQueryClient();

  const create = useMutation<ITransferContract, AxiosError, Partial<ITransferContract>>(
    async (contract: Partial<ITransferContract>) => {
      backdrop.show();
      const { data: apiResult } = await createTransferContract(contract);

      if (!apiResult.success) {
        throw new Error(apiResult.message || apiResult.errorCode);
      }

      return apiResult.data;
    },
    {
      onSuccess: () => {
        snackbar.success('Created successfully.');
        queryClient.invalidateQueries(QueryKey.AllTransferContracts);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  const update = useMutation<ITransferContract, AxiosError, Partial<ITransferContract>>(
    async (contract: Partial<ITransferContract>) => {
      if (!contract.id) {
        throw new Error('id is necessary to update');
      }

      backdrop.show();
      const { data: apiResult } = await updateTransferContract(contract.id, contract);

      if (!apiResult.success) {
        throw new Error(apiResult.message || apiResult.errorCode);
      }

      return apiResult.data;
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Updated successfully.');
        queryClient.invalidateQueries(QueryKey.AllTransferContracts);
        queryClient.setQueryData([QueryKey.TransferContract, variables.id], data);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  const remove = useMutation<ISuccessResult<ITransferContract>, AxiosError, Partial<ITransferContract>>(
    async (contract: Partial<ITransferContract>) => {
      if (!contract.id) {
        throw new Error('id is necessary to remove');
      }

      backdrop.show();

      const { data: apiResult } = await deleteTransferContract(contract.id);
      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult;
    },
    {
      onSuccess: () => {
        snackbar.success('Deleted successfully.');
        queryClient.invalidateQueries(QueryKey.AllTransferContracts);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  return { create, update, remove };
};
